import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import {
  AccountingStatCard,
  CashInflowBar,
  CustomersDueTable,
  TodaysCollections,
  LedgerTable,
} from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { FilterBar, FilterFieldConfig } from '../../../components/FilterBar/FilterBar';
import { useAccountingStore } from '../../../store/accountingStore';
import { downloadLedgerCsv } from '../../../services/accountingApi';
import { triggerBlobDownload } from '../../../utils/downloadBlob';
import {
  AccountingStat,
  CashInflowSegment,
  CustomerDue,
  CollectionItem,
  LedgerEntry as LedgerRow,
  RiskLevel,
  CollectionStatus,
  TransactionType,
} from './accountingTypes';
import styles from './AdminAccounting.module.css';

const SalesIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
  </svg>
);
const TrendIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/>
  </svg>
);
const CreditIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
  </svg>
);
const WarningIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);
const ReceivablesIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z"/>
  </svg>
);
const PaymentModesIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
  </svg>
);

const topIcons = [SalesIcon, TrendIcon, CreditIcon];
const bottomIcons = [WarningIcon, ReceivablesIcon, PaymentModesIcon];

function fmtMoney(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function fmtFull(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((s) => s[0] || '').join('').toUpperCase();
}

function avatarColor(name: string): string {
  const palette = ['#FDE68A', '#BFDBFE', '#D1FAE5', '#FBCFE8', '#DDD6FE'];
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}

const AdminAccounting: React.FC = () => {
  const overview = useAccountingStore((s) => s.overview);
  const customersDueRaw = useAccountingStore((s) => s.customersDue);
  const collectionsRaw = useAccountingStore((s) => s.collections);
  const ledgerRaw = useAccountingStore((s) => s.ledger);
  const ledgerFilters = useAccountingStore((s) => s.ledgerFilters);
  const ledgerLoading = useAccountingStore((s) => s.ledgerLoading);
  const ledgerError = useAccountingStore((s) => s.ledgerError);
  const loading = useAccountingStore((s) => s.loading);
  const error = useAccountingStore((s) => s.error);
  const fetchAll = useAccountingStore((s) => s.fetchAll);
  const setLedgerFilters = useAccountingStore((s) => s.setLedgerFilters);
  const fetchLedger = useAccountingStore((s) => s.fetchLedger);

  const [exporting, setExporting] = useState(false);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Sec 12 — Ledger filters (customer, date range) are real backend filters
  // on GET /accounting/ledger; re-run the server query on change instead of
  // filtering client-side.
  const ledgerFilterFields: FilterFieldConfig[] = useMemo(() => [
    {
      key: 'customer_id',
      type: 'select',
      label: 'All Customers',
      options: customersDueRaw.map((c) => ({ value: String(c.customer_id), label: c.customer_name })),
    },
    { key: 'date_from', type: 'date', label: 'From' },
    { key: 'date_to', type: 'date', label: 'To' },
  ], [customersDueRaw]);

  const ledgerFilterValues = {
    customer_id: ledgerFilters.customer_id ? String(ledgerFilters.customer_id) : '',
    date_from: ledgerFilters.date_from ?? '',
    date_to: ledgerFilters.date_to ?? '',
  };

  const handleLedgerFilterChange = (key: string, value: string) => {
    setLedgerFilters({ [key]: key === 'customer_id' ? (value ? Number(value) : undefined) : (value || undefined) });
    fetchLedger();
  };

  const handleLedgerFilterClear = () => {
    setLedgerFilters({ customer_id: undefined, date_from: undefined, date_to: undefined });
    fetchLedger();
  };

  const handleExportLedger = async () => {
    setExporting(true);
    try {
      const blob = await downloadLedgerCsv(ledgerFilters);
      triggerBlobDownload(blob, `ledger-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      // Surface via the same error banner AsyncBoundary already renders.
      console.error('Ledger export failed', err);
    } finally {
      setExporting(false);
    }
  };

  const topStats: AccountingStat[] = useMemo(() => {
    if (!overview) return [];
    return [
      {
        label: "Today's Sales",
        value: fmtFull(overview.todays_sales),
        subtext: overview.todays_sales_change_pct === 0
          ? 'No change vs yesterday'
          : `${overview.todays_sales_change_pct > 0 ? '+' : ''}${overview.todays_sales_change_pct}% vs yesterday`,
        iconBg: '#ECFDF5',
        iconColor: '#059669',
      },
      {
        label: "Today's Collection",
        value: fmtFull(overview.todays_collection),
        valueColor: '#059669',
        subtext: `${overview.todays_collection_count} payments received`,
        iconBg: '#ECFDF5',
        iconColor: '#059669',
      },
      {
        label: 'Outstanding Receivables',
        value: fmtFull(overview.outstanding_receivables),
        subtext: 'Total balance due',
        iconBg: '#EDE9FE',
        iconColor: '#7C3AED',
      },
    ];
  }, [overview]);

  const bottomStats: AccountingStat[] = useMemo(() => {
    if (!overview) return [];
    return [
      {
        label: 'Overdue Amount',
        value: fmtFull(overview.overdue_amount),
        valueColor: '#DC2626',
        subtext: `${overview.overdue_customer_count} customers overdue`,
        iconBg: '#FEF2F2',
        iconColor: '#DC2626',
      },
      {
        label: 'Expected This Week',
        value: fmtFull(overview.expected_cash_inflow.this_week),
        subtext: 'Invoices due in 7 days',
        iconBg: '#EFF6FF',
        iconColor: '#2563EB',
      },
      {
        label: 'Payment Modes',
        value: '',
        iconBg: '#ECFDF5',
        iconColor: '#059669',
        paymentModes: overview.payment_modes.slice(0, 4).map((m) => ({
          label: m.method.charAt(0).toUpperCase() + m.method.slice(1),
          value: fmtMoney(m.total),
        })),
      },
    ];
  }, [overview]);

  const inflowSegments: CashInflowSegment[] = useMemo(() => {
    if (!overview) return [];
    const i = overview.expected_cash_inflow;
    const total = i.today + i.tomorrow + i.this_week + i.future || 1;
    return [
      { label: 'TODAY', sublabel: 'Today', amount: fmtMoney(i.today), color: '#2563EB', legendColor: '#2563EB', percent: (i.today / total) * 100 },
      { label: 'TOMORROW', sublabel: 'Tomorrow', amount: fmtMoney(i.tomorrow), color: '#0D9488', legendColor: '#0D9488', percent: (i.tomorrow / total) * 100 },
      { label: 'THIS WEEK', sublabel: 'This Week', amount: fmtMoney(i.this_week), color: '#16A34A', legendColor: '#16A34A', percent: (i.this_week / total) * 100 },
      { label: 'FUTURE', sublabel: 'Future', amount: fmtMoney(i.future), color: '#D1D5DB', legendColor: '#9CA3AF', percent: (i.future / total) * 100 },
    ];
  }, [overview]);

  const customersDue: CustomerDue[] = useMemo(() => customersDueRaw.map((c) => {
    const risk: RiskLevel = c.risk === 'block' ? 'Block' : c.risk === 'watch' ? 'Watch' : 'Safe';
    return {
      customer: c.customer_name,
      area: [c.city, c.state].filter(Boolean).join(', '),
      outstanding: fmtMoney(c.outstanding),
      overdueDays: c.oldest_due_days > 0 ? `${c.oldest_due_days} days` : '—',
      creditLimit: '—',
      creditUsedPercent: 0,
      lastPayment: c.last_payment_date
        ? new Date(c.last_payment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '—',
      rep: c.sales_rep_name ?? '—',
      risk,
      aging0_30: c.aging.days_0_30 > 0 ? fmtMoney(c.aging.days_0_30) : '—',
      aging31_60: c.aging.days_31_60 > 0 ? fmtMoney(c.aging.days_31_60) : '—',
      aging61_90: c.aging.days_61_90 > 0 ? fmtMoney(c.aging.days_61_90) : '—',
      aging90Plus: c.aging.days_90_plus > 0 ? fmtMoney(c.aging.days_90_plus) : '—',
    };
  }), [customersDueRaw]);

  const collections: CollectionItem[] = useMemo(() => collectionsRaw.map((c) => {
    const status: CollectionStatus = c.payment_count > 0 ? 'Deposited' : 'Pending';
    return {
      rep: c.rep_name,
      initials: initials(c.rep_name),
      avatarBg: avatarColor(c.rep_name),
      paymentCount: c.payment_count,
      amount: fmtMoney(c.total_amount),
      status,
    };
  }), [collectionsRaw]);

  const ledger: LedgerRow[] = useMemo(() => ledgerRaw.map((l) => {
    const type: TransactionType = l.type === 'invoice' ? 'Invoice' : 'Payment';
    return {
      date: new Date(l.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      customer: l.customer_name,
      type,
      reference: l.reference,
      description: l.description,
      debit: l.debit > 0 ? fmtMoney(l.debit) : '—',
      credit: l.credit > 0 ? fmtMoney(l.credit) : '—',
      balance: '—',
    };
  }), [ledgerRaw]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && !overview}
        error={error}
        empty={false}
        onRetry={() => fetchAll()}
      >
        <div className={styles.statsGrid}>
          {topStats.map((stat, i) => (
            <AccountingStatCard key={stat.label} stat={stat} icon={topIcons[i]} />
          ))}
        </div>

        <div className={styles.statsGrid}>
          {bottomStats.map((stat, i) => (
            <AccountingStatCard key={stat.label} stat={stat} icon={bottomIcons[i]} />
          ))}
        </div>

        <CashInflowBar segments={inflowSegments} />
        <CustomersDueTable items={customersDue} />
        <TodaysCollections items={collections} />
        <FilterBar
          fields={ledgerFilterFields}
          values={ledgerFilterValues}
          onChange={handleLedgerFilterChange}
          onClear={handleLedgerFilterClear}
        />
        <AsyncBoundary loading={ledgerLoading && ledger.length === 0} error={ledgerError} empty={false} onRetry={fetchLedger}>
          <LedgerTable items={ledger} onExport={handleExportLedger} exporting={exporting} />
        </AsyncBoundary>
      </AsyncBoundary>
    </div>
  );
};

export const AdminAccountingPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminAccounting />
  </ProtectedRoute>
);

export { AdminAccounting };
export default AdminAccounting;
